import type { ReactElement } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { Image, Link, SubNav as SubNavSchema } from '@amplience/content-types/typings/c-sub-nav';
import { Separator } from '../../../baseComponents/separator/Separator';
import { useUserContext } from '../../../context/userContext';
import { useLogout } from '../../../hooks/useLogout';
import { ImagePartial } from '../../../partials/image';
import { LinkPartial } from '../../../partials/link';
import { sendNavigationEvent } from '../../../utils/gtm/events/navigation/sendNavigationEvent';
import { getLocalizedValue } from '../../../utils/transformers';
import { Label, LogoutBtn, Nav, NavItem, NavList } from './SubNav.styles';

export type SubNavProps = SubNavSchema & {
  forDesktop?: boolean;
};

function LoginImage({ isVisible }: { isVisible: boolean }) {
  if (!isVisible) return null;
  return <ImagePartial src="Account" width={16} height={16} assetType="static" objectFit="contain" alt="account" />;
}

function NavLink({ link, label, icon, index }: { link: Link; label: string; icon: Image; index: number }) {
  const onClick = () => {
    sendNavigationEvent({
      component_type: 'link ribbon',
      link_text: label,
    });
  };
  return (
    <LinkPartial onClick={onClick} {...link} ariaLabel={label} data-testid={`navBarItemLink-${index + 1}`}>
      <>
        <ImagePartial
          layout="intrinsic"
          altText={icon?.altText}
          src={icon?.image?.name || ''}
          assetType={icon?.assetType}
          height={16}
          width={16}
          objectFit="contain"
          defaultHost={icon?.image?.defaultHost}
          useOriginalSize={icon?.useOriginalSize}
        />
        <Label>{label}</Label>
      </>
    </LinkPartial>
  );
}

function NavLinks({
  subNavComponent,
  forDesktop,
}: {
  subNavComponent: SubNavProps['subNavComponent'];
  forDesktop: boolean;
}) {
  const GLOBAL_NS = 'lib-global-common';
  const { t } = useTranslation(['app-orders-common', GLOBAL_NS]);
  const { isUserLoggedIn, isUserLoggedInLoading } = useUserContext();
  const router = useRouter();
  const { logoutUser } = useLogout();

  const handleLogout = async () => {
    await logoutUser();
  };

  const handleLoginRegister = () => {
    sessionStorage.setItem('lastLocation', router.asPath);
    router.push('/login');
  };

  return (
    <>
      <Separator isVisible={!forDesktop} />

      <Nav $forDesktop={forDesktop} aria-label={t('header.top.navigation', { ns: GLOBAL_NS })}>
        <NavList>
          {!isUserLoggedInLoading &&
            (isUserLoggedIn ? (
              <NavItem key={t('log.out', { ns: GLOBAL_NS })} onClick={handleLogout}>
                <LogoutBtn type="button" data-testid="logoutButton">
                  <LoginImage isVisible={!forDesktop} />
                  <Label>{t('log.out', { ns: GLOBAL_NS })}</Label>
                </LogoutBtn>
              </NavItem>
            ) : (
              <NavItem key={t('login.register', { ns: GLOBAL_NS })} onClick={handleLoginRegister}>
                <LinkPartial internalLink="login" data-testid="headerLoginLink">
                  <LoginImage isVisible={!forDesktop} />
                  <Label>{t('login.register', { ns: GLOBAL_NS })}</Label>
                </LinkPartial>
              </NavItem>
            ))}

          {subNavComponent?.map((subNav, index) => {
            const label = getLocalizedValue(subNav.label);
            return (
              <NavItem key={label} data-testid={`navBarItem-${index + 1}`}>
                <NavLink icon={subNav.icon} link={subNav.link} label={label} index={index} />
              </NavItem>
            );
          })}
        </NavList>
      </Nav>
    </>
  );
}

export function SubNav({ subNavComponent, forDesktop }: SubNavProps): ReactElement {
  return <NavLinks forDesktop={!!forDesktop} subNavComponent={subNavComponent} />;
}
